import React, { useState, useEffect, useMemo } from 'react';
import PanelFFXIV from '@/app/components/PanelFFXIV';
import TextFFXIV from '@/app/components/TextFFXIV';
import ButtonFFXIV from '@/app/components/ButtonFFXIV';
import { useGame } from '@/app/providers/GameContextProvider';
import { useTranslation } from '@/hooks/useTranslation';
import GameModeConfigSeparator from './GameModeConfigSeparator';
import { motion } from 'framer-motion';
import { formatSeedInput, validateSeedFormat, decodeSeed, DecodedSeed } from '@/lib/utils/seedUtils';
import { EXPANSION_CONFIG, MAP_TYPE_CONFIG } from '@/lib/utils/gameConfigUtils';
import Image from 'next/image';
import TooltipFFXIV from '@/app/components/TooltipFFXIV';
import { GameMode } from '@/lib/types/common/GameMode';
import ToggleFFXIV from '@/app/components/ToggleFFXIV';

interface SeedModePanelProps {
    onClose: () => void;
}

const SeedModePanel: React.FC<SeedModePanelProps> = ({ onClose }) => {
    const { room, gameState } = useGame();
    const { t } = useTranslation();
    const [decodedSettings, setDecodedSettings] = useState<DecodedSeed | null>(null);

    const isGameMaster = room?.sessionId === gameState?.gameMasterId;

    // Use synced seed input from game state
    const seedInput = gameState?.seedPanelInput || '';

    // Handler to update seed input (synced to all players)
    const handleSeedInputChange = (value: string) => {
        if (room && isGameMaster) {
            const formatted = formatSeedInput(value);
            room.send("updateSeedInput", { seed: formatted });
        }
    };

    // Decode seed as user types to preview settings
    // Also automatically switch game mode to match the seed
    useEffect(() => {
        if (seedInput && validateSeedFormat(seedInput)) {
            const decoded = decodeSeed(seedInput);
            setDecodedSettings(decoded);

            // Automatically switch game mode to match the seed
            if (decoded && room && isGameMaster && gameState) {
                const currentMode = gameState.gameMode;
                if (currentMode !== decoded.gameMode) {
                    room.send("changeGameMode", { gameMode: decoded.gameMode });
                }
            }
        } else {
            setDecodedSettings(null);
        }
    }, [seedInput, room, isGameMaster, gameState]);

    const handleStartGame = () => {
        if (!room || !seedInput.trim() || !validateSeedFormat(seedInput)) return;
        room.send("startGame", { seed: seedInput.trim() });
    };

    const handleClose = () => {
        onClose();
    };

    const isValidSeed = seedInput.trim() && validateSeedFormat(seedInput);

    // Check if the current game state requires teams (using effective mode after game mode switch)
    // This is more accurate than checking decodedSettings since the game mode has already been switched
    const seedRequiresTeams = useMemo(() => {
        if (!gameState) return false;
        return gameState.isEffectivelyFight || gameState.isEffectivelyCoop;
    }, [gameState?.isEffectivelyFight, gameState?.isEffectivelyCoop]);

    // Check if current game state has teams set up
    const hasTeamsSetUp = useMemo(() => {
        if (!gameState?.teams) return false;
        return gameState.teams.length >= 2;
    }, [gameState?.teams]);

    // Team validation for seeds that require teams
    // Uses gameState.isEffectivelyFight/Coop since the game mode has already been switched
    const teamValidation = useMemo(() => {
        if (!gameState) {
            return { isValid: true, message: null };
        }

        const isFightMode = gameState.isEffectivelyFight;
        const isCoopMode = gameState.isEffectivelyCoop;

        if (!isFightMode && !isCoopMode) {
            return { isValid: true, message: null };
        }

        // Check if teams exist (need at least 2 for fight, at least 1 for coop)
        const minTeams = isFightMode ? 2 : 1;
        if (!gameState.teams || gameState.teams.length < minTeams) {
            return {
                isValid: false,
                message: t('play.seedMode.teamsRequired')
            };
        }

        // Check if all players are in a team
        const playersWithoutTeam: string[] = [];
        gameState.players?.forEach((player) => {
            if (!player.teamId || player.teamId === '') {
                playersWithoutTeam.push(player.name);
            }
        });

        if (playersWithoutTeam.length > 0) {
            return {
                isValid: false,
                message: t('play.seedMode.playersNeedTeam', { count: playersWithoutTeam.length })
            };
        }

        // Check if each team has at least one player
        const emptyTeams: string[] = [];
        gameState.teams.forEach(team => {
            const memberCount = team.memberSessionIds?.length || 0;
            if (memberCount === 0) {
                emptyTeams.push(team.name);
            }
        });

        if (emptyTeams.length > 0) {
            return {
                isValid: false,
                message: t('play.seedMode.teamsNeedPlayers', { count: emptyTeams.length })
            };
        }

        // For Coop mode, each team needs exactly 2 players
        if (isCoopMode) {
            const invalidTeams: string[] = [];
            gameState.teams.forEach(team => {
                const memberCount = team.memberSessionIds?.length || 0;
                if (memberCount !== 2) {
                    invalidTeams.push(team.name);
                }
            });

            if (invalidTeams.length > 0) {
                return {
                    isValid: false,
                    message: t('play.seedMode.coopTeamsNeedTwoPlayers', { count: invalidTeams.length })
                };
            }
        }

        return { isValid: true, message: null };
    }, [gameState, t]);

    // Check if current teams will be ignored (seed doesn't need teams but teams exist)
    const teamsWillBeIgnored = useMemo(() => {
        if (!gameState) return false;
        const seedNeedsTeams = gameState.isEffectivelyFight || gameState.isEffectivelyCoop;
        return !seedNeedsTeams && hasTeamsSetUp;
    }, [gameState?.isEffectivelyFight, gameState?.isEffectivelyCoop, hasTeamsSetUp]);

    const canStartGame = isGameMaster && isValidSeed && teamValidation.isValid;

    // Team management handlers
    const numberOfTeams = gameState?.teams?.length || 0;
    const isFightMode = gameState?.isEffectivelyFight || false;
    const minTeams = isFightMode ? 2 : 1;

    const handleNumberOfTeamsChange = (newTeams: number) => {
        if (!room || !isGameMaster) return;

        const currentTeams = gameState?.teams?.length || 0;

        if (newTeams > currentTeams) {
            // Create additional teams
            for (let i = currentTeams; i < newTeams; i++) {
                room.send('createTeam');
            }
        } else if (newTeams < currentTeams) {
            // Remove extra teams (from the end)
            const teamsToRemove = Array.from(gameState?.teams || []).slice(newTeams);
            teamsToRemove.forEach(team => {
                room.send('removeTeam', { teamId: team.id });
            });
        }
    };

    // Helper to get game mode display name
    const getGameModeName = (mode: GameMode): string => {
        return t(`game.modes.${mode}.name`);
    };

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
        >
            <PanelFFXIV className="w-[380px] flex flex-col gap-4 !pt-1 !px-5 !pb-4">
                <div className="w-full text-center">
                    <TextFFXIV className="text-lg font-medium" text={t("play.seedMode.title")} />
                    <GameModeConfigSeparator />
                </div>

                <div className="flex flex-col gap-4">
                    <TextFFXIV text={t("play.seedMode.description")} className="text-center whitespace-normal max-w-[380px]" />
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={seedInput}
                            onChange={(e) => handleSeedInputChange(e.target.value)}
                            placeholder="XXXX-XXX-XXXX-XXXXX-XXXX"
                            disabled={!isGameMaster}
                            className={`
                                    flex-1 bg-[#4C4C4C] text-white text-center py-2 px-3 rounded
                                    shadow-inner shadow-black/50 font-mono text-lg tracking-widest
                                    placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#c0a270]
                                    ${!isGameMaster ? 'opacity-50 cursor-not-allowed' : ''}
                                `}
                            maxLength={25}
                        />
                    </div>

                    {/* Show decoded settings preview */}
                    {decodedSettings && (
                        <div className="rounded p-3 flex flex-col gap-3 bg-[#2a2a3a] border border-[#4a4a6a]">
                            <div className="flex flex-col gap-x-4 gap-y-1 text-sm">
                                <div className="flex justify-between">
                                    <TextFFXIV text={t("play.recap.gameMode")} className="" />
                                    <TextFFXIV text={getGameModeName(decodedSettings.gameMode)} className="" />
                                </div>

                                {decodedSettings.maxRounds !== -1 && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.rounds")} className="" />
                                        <TextFFXIV text={decodedSettings.maxRounds.toString()} className="" />
                                    </div>
                                )}

                                {decodedSettings.timerSeconds > 0 && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.timePerRound")} className="" />
                                        <TextFFXIV text={`${decodedSettings.timerSeconds}s`} className="" />
                                    </div>
                                )}

                                {decodedSettings.gameMode === GameMode.FLASH && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.flashDuration")} className="" />
                                        <TextFFXIV text={`${decodedSettings.flashDuration}ms`} className="" />
                                    </div>
                                )}

                                {decodedSettings.gameMode === GameMode.HAZARD && (
                                    <div className="flex justify-between">
                                        <TextFFXIV text={t("play.recap.hazardsPerRound")} className="" />
                                        <TextFFXIV text={decodedSettings.hazardsPerRound.toString()} className="" />
                                    </div>
                                )}
                            </div>

                            <GameModeConfigSeparator />

                            <div className="flex flex-col gap-4">
                                {/* Expansions */}
                                <div className="flex justify-between items-center gap-2 grow">
                                    <TextFFXIV text={t("play.expansions")} className="text-sm" />
                                    <div className="flex gap-2 flex-wrap">
                                        {EXPANSION_CONFIG.map((config) => {
                                            const isSelected = decodedSettings.expansions.includes(config.expansion);
                                            return (
                                                <div key={config.expansion} className="group relative">
                                                    <div className={`${!isSelected ? 'opacity-30' : ''} relative w-6 h-6`}>
                                                        {isSelected && (
                                                            <div className="left-[-2px] top-[-2px] rounded-md absolute w-[28px] h-[28px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                                        )}
                                                        <Image
                                                            src={config.icon}
                                                            alt={t(config.name)}
                                                            width={24}
                                                            height={24}
                                                            className="w-6 h-6 shadow shadow-black/50 rounded"
                                                        />
                                                    </div>
                                                    <TooltipFFXIV><span className="text-white">{t(config.name)}</span></TooltipFFXIV>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Map Types */}
                                <div className="flex justify-between items-center gap-2 grow">
                                    <TextFFXIV text={t("play.mapSets")} className="text-sm" />
                                    <div className="flex gap-2 flex-wrap">
                                        {MAP_TYPE_CONFIG.map((config) => {
                                            const isSelected = decodedSettings.mapTypes.includes(config.mapType);
                                            return (
                                                <div key={config.mapType} className="group relative">
                                                    <div className={`${!isSelected ? 'opacity-30' : ''} relative w-6 h-6 rounded shadow shadow-black/50`}>
                                                        {isSelected && (
                                                            <div className="left-[-2px] top-[-2px] rounded-md absolute w-[28px] h-[28px] z-40 border-2 border-[#CDCCA8] shadow-[inset_0px_0px_2px_2px_rgba(0,0,0,0.6),0px_0px_2px_2px_rgba(0,0,0,0.5)]"></div>
                                                        )}
                                                        <Image
                                                            src={config.icon}
                                                            alt={t(config.name)}
                                                            width={24}
                                                            height={24}
                                                        />
                                                    </div>
                                                    <TooltipFFXIV><span className="text-white">{t(`game.config.mapTypes.${config.mapType}`)}</span></TooltipFFXIV>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Warning/Error messages for team requirements */}
                    {decodedSettings && seedRequiresTeams && !teamValidation.isValid && (
                        <div className="rounded p-3 bg-red-900/30 border border-red-500/50">
                            <TextFFXIV
                                text={teamValidation.message || ''}
                                className="text-red-300 text-sm text-center"
                            />
                        </div>
                    )}

                    {/* Team Setup Section - shown when seed requires teams */}
                    {decodedSettings && seedRequiresTeams && (
                        <div className="rounded p-3 flex flex-col gap-3 bg-[#2a2a3a] border border-[#4a4a6a]">
                            <TextFFXIV text={t('play.seedMode.teamSetup')} className="text-center font-medium" />

                            {/* Number of teams selector - only for game master */}
                            {isGameMaster && (
                                <div className="flex justify-between items-center gap-2">
                                    <TextFFXIV text={t('game.config.advancedSettings.numberOfTeams')} className="text-sm" />
                                    <div className="flex gap-3">
                                        {Array.from({ length: 4 - minTeams + 1 }, (_, i) => i + minTeams).map((num) => (
                                            <ToggleFFXIV
                                                key={num}
                                                value={numberOfTeams === num}
                                                onChange={() => handleNumberOfTeamsChange(num)}
                                                shape='circle'
                                                text={num.toString()}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Info for non-game masters */}
                            {!isGameMaster && (
                                <TextFFXIV
                                    text={t('play.seedMode.joinTeamInLobby')}
                                    className="text-sm text-center text-gray-400"
                                />
                            )}
                        </div>
                    )}

                    {/* Info message when teams will be ignored */}
                    {decodedSettings && teamsWillBeIgnored && (
                        <div className="rounded p-3 bg-yellow-900/30 border border-yellow-500/50">
                            <TextFFXIV
                                text={t('play.seedMode.teamsWillBeIgnored')}
                                className="text-yellow-300 text-sm text-center"
                            />
                        </div>
                    )}

                    <div className="flex gap-3 justify-center">
                        {isGameMaster && (
                            <ButtonFFXIV
                                onClick={handleClose}
                                text={t("play.seedMode.back")}
                                fitContent={true}
                                className="!px-5"
                            />
                        )}
                        <ButtonFFXIV
                            text={t("play.seedMode.startGame")}
                            onClick={handleStartGame}
                            disabled={!canStartGame}
                            fitContent={true}
                            className="!px-7"
                        />
                    </div>

                </div>
            </PanelFFXIV>
        </motion.div>
    );
};

export default SeedModePanel;
